/**
 * +world-resources: breakables, hidden caches and reagent nodes (docs/design/world-resources.md).
 *
 * Objects come from DungeonDef.worldObjects and never block movement or pathing (they are not
 * units and not obstacles). Only the LOOTER (the human; a bot only in autopilot/spectate) breaks
 * them and collects what they drop, so companions never steal pickups and all-bot harness runs
 * are untouched.
 *
 * Determinism: every roll here uses its own seeded stream keyed by (run seed, object id), never
 * the world RNG, so loot is identical whatever order things break in and combat RNG is unchanged.
 *
 * Tick hook: core calls `tickWorldLoot` after deaths, before M+ bookkeeping. It reads the tick's
 * pending events (looter damage => splash around the struck target; looter casts => touch reach)
 * and advances caches, reveals and the pickup magnet.
 */
import type {
  DungeonDef,
  PickupRarity,
  PickupState,
  SimContext,
  UnitId,
  Vec2,
  World,
  WorldFinds,
  WorldLootEntry,
  WorldLootStats,
  WorldObjectDef,
  WorldObjectState,
  WorldResourceKind,
} from '../contracts';
import {
  CACHE_OPEN_MS,
  PICKUP_COLLECT_RADIUS,
  PICKUP_FRESH_MAGNET_RADIUS,
  PICKUP_FRESH_MS,
  PICKUP_MAGNET_RADIUS,
  PICKUP_RARITY,
  TICK_MS,
  WORLD_BUMP_REACH,
  WORLD_HIT_SPLASH,
  WORLD_REVEAL_RADIUS,
  WORLD_TOUCH_REACH,
} from '../contracts';
import { createSeededRng } from '../core/rng';
import { WISP_TOUCH, wispAmount } from '../field/loot'; // +field

/** Extra splash for AoE hits (yd). */
const AOE_SPLASH_BONUS = 1.5;
/** Burst: arc time of the first pickup and the stagger between pickups (ms). */
const ARC_MS = 520;
const ARC_STAGGER_MS = 70;
/** Burst ring (yd from the object's centre). */
const SCATTER_MIN = 0.9;
const SCATTER_MAX = 2.4;
/** Magnet speed: base + acceleration (yd/s, yd/s²). */
const MAGNET_SPEED = 9;
const MAGNET_ACCEL = 45;
/** At most this many pickups per resource line (amounts merge beyond it). */
const MAX_PILES: Partial<Record<WorldResourceKind, number>> = { gold: 3, fangsteel: 4 };
const DEFAULT_MAX_PILES = 3;

const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** FNV-1a of a string mixed with the run seed (32-bit). */
export function objectSeed(seed: number, id: string, salt = 0): number {
  let h = (0x811c9dc5 ^ (seed >>> 0) ^ Math.imul(salt + 1, 0x9e3779b1)) >>> 0;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * The loot an object holds on this run: a pure function of (run seed, key level, object). Every
 * entry always draws twice (chance, amount) so adding a level gate never shifts later lines.
 */
export function rollObjectLoot(seed: number, level: number, def: Pick<WorldObjectDef, 'id' | 'loot'>): { resource: WorldResourceKind; amount: number }[] {
  const rng = createSeededRng(objectSeed(seed, def.id));
  const out: { resource: WorldResourceKind; amount: number }[] = [];
  for (const e of def.loot) {
    const hit = rng.next() < e.chance;
    const amount = e.min + Math.floor(rng.next() * (e.max - e.min + 1));
    if (!hit || amount <= 0 || (e.minLevel !== undefined && level < e.minLevel)) continue;
    const prev = out.find((o) => o.resource === e.resource);
    if (prev) prev.amount += amount;
    else out.push({ resource: e.resource, amount });
  }
  return out;
}

/** Expected amount per resource of a loot table at a key level (tuning, docs, harness). */
export function expectedObjectLoot(level: number, loot: readonly WorldLootEntry[]): WorldFinds {
  const out: WorldFinds = {};
  for (const e of loot) {
    if (e.minLevel !== undefined && level < e.minLevel) continue;
    out[e.resource] = (out[e.resource] ?? 0) + e.chance * ((e.min + e.max) / 2);
  }
  return out;
}

/** Sum of finds (pure). */
export function addFinds(...all: (WorldFinds | null | undefined)[]): WorldFinds {
  const out: WorldFinds = {};
  for (const f of all) {
    if (!f) continue;
    for (const [k, v] of Object.entries(f) as [WorldResourceKind, number][]) if (v) out[k] = (out[k] ?? 0) + v;
  }
  return out;
}

/** Default looter: the first human party member (none in an all-bot run). */
export function defaultLooter(world: World): UnitId | null {
  return world.partyIds.find((id) => world.units[id]?.controller === 'human') ?? null;
}

/** Build the world-object state from the dungeon (called once by createSim). */
export function initWorldLoot(world: World, dungeon: DungeonDef | undefined, looterId: UnitId | null): void {
  const defs = dungeon?.worldObjects ?? [];
  // +field: field maps with ground loot get the (empty) machinery too: chests and drops come later.
  if (!defs.length && !dungeon?.field?.loot) return;
  world.worldObjects = defs.map(
    (d): WorldObjectState => ({
      id: d.id,
      kind: d.kind,
      class: d.class,
      pos: { ...d.pos },
      facing: d.facing ?? 0,
      roomId: d.roomId,
      radius: d.radius,
      hp: Math.max(1, d.hp),
      maxHp: Math.max(1, d.hp),
      hidden: !!d.hidden,
      discovered: !d.hidden,
      broken: false,
      brokenAt: null,
      lastHitAt: null,
      openProgressMs: 0,
      variant: d.variant ?? 0,
    }),
  );
  world.pickups = [];
  world.worldLoot = {
    looterId,
    collected: {},
    stats: {
      broken: 0,
      breakables: defs.filter((d) => d.class === 'breakable').length,
      cachesOpened: 0,
      caches: defs.filter((d) => d.class === 'cache').length,
      nodes: defs.filter((d) => d.class === 'node').length,
      nodesBroken: 0,
      pickups: 0,
    },
  };
}

const defOf = (ctx: SimContext, id: string): WorldObjectDef | undefined => ctx.content.dungeon(ctx.world.dungeonId)?.worldObjects?.find((d) => d.id === id);

/** Is the object within `reach` (edge to edge) of the unit? */
function inReach(world: World, unitId: UnitId, o: WorldObjectState, reach: number): boolean {
  const u = world.units[unitId];
  return !!u && dist(u.pos, o.pos) - o.radius - u.radius <= reach && atHeight(u, o);
}

/** +glide: an object up on a platform is reached only from up there (standing on it, or hovering level with it). */
function atHeight(u: { perch?: { z: number }; jump?: { air?: { z: number } } }, o: { z?: number }): boolean {
  if (o.z === undefined) return true;
  const z = u.perch?.z ?? u.jump?.air?.z;
  return z !== undefined && Math.abs(z - o.z) <= SKY_REACH_Z;
}
/** +glide: height slack for reaching a raised object. */
const SKY_REACH_Z = 1.6;

/** Objects the looter can break / open right now, nearest first (web input: "interact" key). */
export function objectsInReach(world: Readonly<World>, unitId: UnitId, reach = WORLD_TOUCH_REACH): WorldObjectState[] {
  const u = world.units[unitId];
  if (!u || !world.worldObjects) return [];
  return (world.worldObjects as WorldObjectState[])
    .filter((o) => !o.broken && o.discovered && dist(u.pos, o.pos) - o.radius - u.radius <= reach && atHeight(u, o))
    .sort((a, b) => dist(u.pos, a.pos) - dist(u.pos, b.pos) || (a.id < b.id ? -1 : 1));
}

function breakObject(ctx: SimContext, o: WorldObjectState, by: UnitId): void {
  const w = ctx.world;
  const loot = w.worldLoot!;
  o.broken = true;
  o.hp = 0;
  o.brokenAt = ctx.now;
  o.openProgressMs = 0;
  if (o.class === 'cache') loot.stats.cachesOpened += 1;
  else if (o.class === 'node') loot.stats.nodesBroken += 1;
  else loot.stats.broken += 1;
  const def = defOf(ctx, o.id);
  // +field: a field chest carries the loot it rolled when it spawned.
  const lines: { resource: WorldResourceKind; amount: number; rarity?: PickupRarity }[] = o.loot ? o.loot.map((l) => ({ ...l })) : def ? rollObjectLoot(w.seed, w.mplus.level, def) : [];
  // Split each line into piles (more pieces = a better burst), then scatter on a ring.
  const piles: { resource: WorldResourceKind; amount: number; rarity?: PickupRarity }[] = [];
  for (const l of lines) {
    const n = l.resource === 'xp' || l.resource === 'item' ? 1 : Math.max(1, Math.min(l.amount, MAX_PILES[l.resource] ?? DEFAULT_MAX_PILES));
    const base = Math.floor(l.amount / n);
    for (let i = 0; i < n; i++) piles.push({ resource: l.resource, amount: base + (i < l.amount - base * n ? 1 : 0), ...(l.rarity ? { rarity: l.rarity } : {}) });
  }
  const rng = createSeededRng(objectSeed(w.seed, o.id, 1));
  const start = rng.next() * Math.PI * 2;
  ctx.emit({ type: 'object_break', sourceID: by, objectId: o.id, kind: o.kind, objectClass: o.class, pos: { ...o.pos }, drops: piles.length });
  piles.forEach((pile, i) => {
    const ang = start + (i / Math.max(1, piles.length)) * Math.PI * 2 + (rng.next() - 0.5) * 0.9;
    let r = (SCATTER_MIN + rng.next() * (SCATTER_MAX - SCATTER_MIN)) * (o.z !== undefined ? 0.35 : 1); // +glide: stays on the platform
    let pos = { x: o.pos.x + Math.cos(ang) * r, y: o.pos.y + Math.sin(ang) * r };
    while (r > 0.2 && !ctx.sys.spatial.isWalkable(w, pos)) {
      r *= 0.6;
      pos = { x: o.pos.x + Math.cos(ang) * r, y: o.pos.y + Math.sin(ang) * r };
    }
    if (!ctx.sys.spatial.isWalkable(w, pos)) pos = { ...o.pos };
    pos = { x: Math.round(pos.x * 100) / 100, y: Math.round(pos.y * 100) / 100 };
    const p: PickupState = {
      id: `${o.id}#${i}`,
      resource: pile.resource,
      amount: pile.amount,
      rarity: pile.rarity ?? PICKUP_RARITY[pile.resource],
      objectId: o.id,
      from: { ...o.pos },
      pos,
      spawnedAt: ctx.now,
      landAt: ctx.now + ARC_MS + i * ARC_STAGGER_MS,
      magnetSince: null,
      ...(o.z !== undefined ? { z: o.z } : {}), // +glide
    };
    w.pickups!.push(p);
    ctx.emit({ type: 'pickup_spawn', pickupId: p.id, objectId: o.id, resource: p.resource, amount: p.amount, rarity: p.rarity, from: p.from, pos: p.pos, landAt: p.landAt });
  });
}

/** One hit on a breakable/node by the looter (at most once per object per tick). */
function hitObject(ctx: SimContext, o: WorldObjectState, by: UnitId): void {
  if (o.broken || o.class === 'cache' || o.lastHitAt === ctx.now) return;
  o.lastHitAt = ctx.now;
  o.hp -= 1;
  if (o.hp <= 0) breakObject(ctx, o, by);
  else ctx.emit({ type: 'object_hit', sourceID: by, objectId: o.id, kind: o.kind, hp: o.hp });
}

/**
 * The 'interact' command on an object (GCD-free, no cast): one hit on a breakable/node in
 * reach; on a cache it is a no-op beyond what standing there already does. Returns true when
 * something happened.
 */
export function interactObject(ctx: SimContext, unitId: UnitId, objectId: string): boolean {
  const w = ctx.world;
  const loot = w.worldLoot;
  const u = w.units[unitId];
  if (!loot || !u?.alive || loot.looterId !== unitId) return false;
  const o = w.worldObjects?.find((x) => x.id === objectId);
  if (!o || o.broken || !o.discovered || !inReach(w, unitId, o, WORLD_TOUCH_REACH)) return false;
  if (o.class === 'cache') return true;
  hitObject(ctx, o, unitId);
  return true;
}

/** Per-tick update (core tick order 9b). */
export function tickWorldLoot(ctx: SimContext): void {
  const w = ctx.world;
  const loot = w.worldLoot;
  const objects = w.worldObjects;
  if (!loot || !objects || !loot.looterId) return;
  const me = w.units[loot.looterId];
  if (!me?.alive) return;

  // 1. Hits from this tick's looter events.
  for (const e of w.events) {
    if (e.type === 'damage' && e.sourceID === me.id) {
      const t = w.units[e.targetID];
      if (!t) continue;
      const splash = WORLD_HIT_SPLASH + (e.isAoE ? AOE_SPLASH_BONUS : 0) + t.radius;
      for (const o of objects) if (!o.broken && o.class !== 'cache' && o.discovered && dist(t.pos, o.pos) - o.radius <= splash) hitObject(ctx, o, me.id);
    } else if (e.type === 'cast' && e.sourceID === me.id) {
      // A swing at the air: clutter in front of the looter within touch reach.
      const fx = Math.cos(me.facing);
      const fy = Math.sin(me.facing);
      for (const o of objects) {
        if (o.broken || o.class === 'cache' || !o.discovered || !inReach(w, me.id, o, WORLD_TOUCH_REACH)) continue;
        const dx = o.pos.x - me.pos.x;
        const dy = o.pos.y - me.pos.y;
        if (dx * fx + dy * fy >= -o.radius) hitObject(ctx, o, me.id);
      }
    }
  }

  // 1b. Running into clutter kicks it over (breakables only; nodes need real hits).
  if (me.moving) for (const o of objects) if (!o.broken && o.class === 'breakable' && inReach(w, me.id, o, WORLD_BUMP_REACH)) hitObject(ctx, o, me.id);

  // 2. Reveals and caches.
  for (const o of objects) {
    if (o.broken) continue;
    if (!o.discovered && dist(me.pos, o.pos) <= WORLD_REVEAL_RADIUS) {
      o.discovered = true;
      ctx.emit({ type: 'object_discovered', sourceID: me.id, objectId: o.id, kind: o.kind, pos: { ...o.pos } });
    }
    if (o.class !== 'cache' || !o.discovered) continue;
    if (inReach(w, me.id, o, WORLD_TOUCH_REACH)) {
      if (o.openProgressMs === 0) ctx.emit({ type: 'cache_open', sourceID: me.id, objectId: o.id, state: 'start', durationMs: CACHE_OPEN_MS });
      o.openProgressMs += TICK_MS;
      if (o.openProgressMs >= CACHE_OPEN_MS) breakObject(ctx, o, me.id);
    } else if (o.openProgressMs > 0) {
      o.openProgressMs = 0;
      ctx.emit({ type: 'cache_open', sourceID: me.id, objectId: o.id, state: 'cancel', durationMs: CACHE_OPEN_MS });
    }
  }

  // 3. Pickups: magnet, then collect.
  const pickups = w.pickups!;
  if (!pickups.length) return;
  const keep: PickupState[] = [];
  for (const p of pickups) {
    if (ctx.now < p.landAt) {
      keep.push(p);
      continue;
    }
    let d = dist(me.pos, p.pos);
    // +field: XP wisps float where they are: run through them (chained wisps pay more).
    if (p.wisp) {
      if (d > WISP_TOUCH + me.radius) {
        keep.push(p);
        continue;
      }
      const { amount, chain } = wispAmount(ctx, p);
      const total = (loot.collected.xp ?? 0) + amount;
      loot.collected.xp = total;
      loot.stats.pickups += 1;
      ctx.emit({ type: 'pickup', sourceID: me.id, pickupId: p.id, resource: 'xp', amount, rarity: p.rarity, pos: { ...p.pos }, total, chain });
      continue;
    }
    const magnetR = ctx.now - p.landAt <= PICKUP_FRESH_MS ? PICKUP_FRESH_MAGNET_RADIUS : PICKUP_MAGNET_RADIUS;
    if (p.magnetSince === null && d <= magnetR) p.magnetSince = ctx.now;
    if (p.magnetSince !== null && d > PICKUP_COLLECT_RADIUS) {
      const t = (ctx.now - p.magnetSince) / 1000;
      const stepYd = ((MAGNET_SPEED + MAGNET_ACCEL * t) * TICK_MS) / 1000;
      const k = Math.min(1, stepYd / d);
      p.pos = { x: p.pos.x + (me.pos.x - p.pos.x) * k, y: p.pos.y + (me.pos.y - p.pos.y) * k };
      d = dist(me.pos, p.pos);
    }
    if (d <= PICKUP_COLLECT_RADIUS) {
      const total = (loot.collected[p.resource] ?? 0) + p.amount;
      loot.collected[p.resource] = total;
      loot.stats.pickups += 1;
      ctx.emit({ type: 'pickup', sourceID: me.id, pickupId: p.id, resource: p.resource, amount: p.amount, rarity: p.rarity, pos: { ...p.pos }, total });
    } else keep.push(p);
  }
  w.pickups = keep;
}

/** Run-result fields (mplus.result). */
export function worldLootResult(world: World): { worldFinds?: WorldFinds; worldStats?: WorldLootStats } {
  if (!world.worldLoot) return {};
  return { worldFinds: { ...world.worldLoot.collected }, worldStats: { ...world.worldLoot.stats } };
}

